import React, { useCallback, ComponentProps, useMemo } from "react";
import { ChromePicker, ColorResult } from "react-color";
import {
  PopupMenu,
  Flex,
  Text,
  Button,
  Tooltip,
  Box,
  buildPath,
  SpacePermission,
  ItemType,
  KitPermission,
  PortalPermission,
  Kit,
  Portal,
  utils,
  useStoredState,
} from "@thenounproject/lingo-core";
import styled, { useTheme } from "styled-components";

import { useSelectSpace } from "@selectors/entities/spaces";
import useUpsell, { AccessStatus } from "@hooks/useUpsell";
import UpsellTooltip from "../../../components/spaces/settings/UpsellTooltip";
import useNavPoint from "@hooks/useNavPoint";
import useConfigureMode from "../../../components/hooks/useConfigureMode";

const { getColor } = utils;

const StyledColorPicker = styled(ChromePicker)`
  width: 100% !important;
`;

const RECENT_COLOR_COUNT = 3;

function kitPortalContext(kit: Kit, portal: Portal) {
  const canEditKit = kit && kit.access.permissions.includes(KitPermission.manageCustomTheme);
  const canEditPortal =
    !canEditKit &&
    !kit &&
    portal &&
    portal.access.permissions.includes(PortalPermission.manageCustomTheme);

  return { canEditKit, canEditPortal };
}

type NoteProps = {
  editItemBackground: (background: string | null, itemType?: ItemType) => void;
  selectedPreset: string | null | undefined;
  hPos?: ComponentProps<typeof PopupMenu.Item>["hPos"];
  nested?: boolean;
};

export const NoteBackgroundColorOptions: React.FC<NoteProps> = ({
  editItemBackground,
  selectedPreset,
  hPos,
  nested = true,
}) => {
  const space = useSelectSpace();
  const theme = useTheme();
  const themeAccess = useUpsell(SpacePermission.manageCustomTheme);
  const { toggleConfigureMode } = useConfigureMode();
  const { portal, kit } = useNavPoint();

  const infoColor =
    theme?.active && theme.noteInfoColor ? theme.noteInfoColor : getColor("noteInfo");
  const warningColor =
    theme?.active && theme.noteWarningColor ? theme.noteWarningColor : getColor("noteWarning");
  const successColor =
    theme?.active && theme.noteSuccessColor ? theme.noteSuccessColor : getColor("noteSuccess");
  const neutralColor =
    theme?.active && theme.noteNeutralColor ? theme.noteNeutralColor : getColor("noteNeutral");
  const pickerColor = selectedPreset?.startsWith("#") ? selectedPreset : "#ff0000";

  const { canEditKit, canEditPortal } = kitPortalContext(kit, portal);
  const canEditTheme =
    canEditKit || canEditPortal || themeAccess.accessStatus !== AccessStatus.insufficientRole;
  const mustUpgrade =
    !canEditKit && !canEditPortal && themeAccess.accessStatus === AccessStatus.insufficientPlan;

  const [recentColors, setRecentColors] = useStoredState<string[]>(
    `note-background-recent-colors-${space.id}`,
    []
  );

  const themeEditorButtonProps = useMemo(() => {
    const props: Partial<ComponentProps<typeof Button>> = {};
    if (canEditKit || canEditPortal) {
      props.onClick = toggleConfigureMode;
    } else {
      props.link = buildPath("/settings/editor?view=gallery", { space });
    }
    return props;
  }, [canEditKit, canEditPortal, toggleConfigureMode, space]);

  const onCustomColorChange = useCallback(
    (color: ColorResult) => {
      editItemBackground(color.hex, ItemType.note);
      setRecentColors(prevColors => {
        const newColors = [color.hex.toUpperCase(), ...prevColors.filter(c => c !== color.hex)];
        return newColors.slice(0, RECENT_COLOR_COUNT); // Keep only the 3 most recent colors
      });
    },
    [editItemBackground, setRecentColors]
  );

  const renderMenu = useCallback(() => {
    return (
      <>
        <PopupMenu.Section title="Presets">
          <PopupMenu.Item
            key="none"
            title="None"
            onClick={() => editItemBackground("clear", ItemType.note)}
            checked={!selectedPreset || selectedPreset === "default" || selectedPreset === "clear"}
            image={{ color: "transparent" }}
          />
          <PopupMenu.Item
            key="info"
            title="Info"
            onClick={() => editItemBackground("noteInfoColor", ItemType.note)}
            checked={selectedPreset === "noteInfoColor"}
            image={{ color: infoColor }}
          />
          <PopupMenu.Item
            key="warning"
            title="Warning"
            onClick={() => editItemBackground("noteWarningColor", ItemType.note)}
            checked={selectedPreset === "noteWarningColor"}
            image={{ color: warningColor }}
          />
          <PopupMenu.Item
            key="success"
            title="Success"
            onClick={() => editItemBackground("noteSuccessColor", ItemType.note)}
            checked={selectedPreset === "noteSuccessColor"}
            image={{ color: successColor }}
          />
          <PopupMenu.Item
            key="neutral"
            title="Neutral"
            onClick={() => editItemBackground("noteNeutralColor", ItemType.note)}
            checked={selectedPreset === "noteNeutralColor"}
            image={{ color: neutralColor }}
          />
        </PopupMenu.Section>
        <PopupMenu.Section title={recentColors.length > 0 ? "Recent colors" : null}>
          {recentColors.length > 0 && (
            <>
              {recentColors.map(color => (
                <PopupMenu.Item
                  key={color}
                  title={color}
                  onClick={() => editItemBackground(color, ItemType.note)}
                  checked={selectedPreset === color}
                  image={{ color }}
                />
              ))}
            </>
          )}
          <PopupMenu.Item
            key="custom"
            title="Custom"
            image={{ iconId: "content.custom-colors", fill: "", size: "16" }}
            checked={selectedPreset?.startsWith("#") && !recentColors.includes(selectedPreset)}>
            <Box onClick={e => e.stopPropagation()}>
              <StyledColorPicker
                color={pickerColor}
                onChangeComplete={onCustomColorChange}
                disableAlpha
              />
            </Box>
          </PopupMenu.Item>
        </PopupMenu.Section>
        {canEditTheme && (
          <PopupMenu.Section data-tooltip-source="theme-editor-button">
            <Flex background="grayLightest" p="8px" alignItems="center" flexDirection="column">
              <Text font="ui.small" textAlign="center">
                Configure preset colors in the
              </Text>
              <Button
                text="theme editor"
                buttonStyle="tertiary"
                size="small"
                disabled={mustUpgrade}
                {...themeEditorButtonProps}
              />
            </Flex>
            {mustUpgrade && (
              <UpsellTooltip
                onClick={themeAccess.openUpgradeModal}
                source="theme-editor-button"
                featureName="Custom themes"
                direction={Tooltip.Direction.Top}
              />
            )}
          </PopupMenu.Section>
        )}
      </>
    );
  }, [
    selectedPreset,
    infoColor,
    warningColor,
    successColor,
    neutralColor,
    recentColors,
    pickerColor,
    onCustomColorChange,
    canEditTheme,
    mustUpgrade,
    themeEditorButtonProps,
    themeAccess.openUpgradeModal,
    editItemBackground,
  ]);

  if (!nested) {
    return renderMenu();
  }

  return (
    <PopupMenu.Item key="backgroundColor" title="Background color" hPos={hPos}>
      {renderMenu()}
    </PopupMenu.Item>
  );
};

type AssetProps = {
  editAssetBackground: (background: string | null) => void;
  selectedPreset: string | null | undefined;
  hPos?: ComponentProps<typeof PopupMenu.Item>["hPos"];
  optionsOnly?: boolean;
};

export const AssetBackgroundColorOptions: React.FC<AssetProps> = ({
  editAssetBackground,
  selectedPreset,
  hPos,
  optionsOnly = false,
}) => {
  const { space, kit, portal } = useNavPoint();
  const { toggleConfigureMode } = useConfigureMode();
  const theme = useTheme();
  const themeAccess = useUpsell(SpacePermission.manageCustomTheme);

  const activeCustomTheme = theme?.active;
  const lightColor =
    activeCustomTheme && theme.assetLightColor ? theme.assetLightColor : getColor("grayLighter");
  const darkColor =
    activeCustomTheme && theme.assetDarkColor ? theme.assetDarkColor : getColor("grayDark");
  const pickerColor = selectedPreset?.startsWith("#") ? selectedPreset : "#ff0000";

  const { canEditKit, canEditPortal } = kitPortalContext(kit, portal);
  const canEditTheme =
    canEditKit || canEditPortal || themeAccess.accessStatus !== AccessStatus.insufficientRole;
  const mustUpgrade =
    !canEditKit && !canEditPortal && themeAccess.accessStatus === AccessStatus.insufficientPlan;

  const [recentColors, setRecentColors] = useStoredState<string[]>(
    `asset-background-recent-colors-${space.id}`,
    []
  );

  const onCustomColorChange = useCallback(
    (color: ColorResult) => {
      editAssetBackground(color.hex);
      setRecentColors(prevColors => {
        const newColors = [color.hex, ...prevColors.filter(c => c !== color.hex)];
        return newColors.slice(0, RECENT_COLOR_COUNT); // Keep only the 3 most recent colors
      });
    },
    [editAssetBackground, setRecentColors]
  );

  const themeEditorButtonProps: Partial<ComponentProps<typeof Button>> = {};
  if (canEditKit || canEditPortal) {
    themeEditorButtonProps.onClick = toggleConfigureMode;
  } else {
    themeEditorButtonProps.link = buildPath("/settings/editor?view=gallery", { space });
  }

  function renderMenu() {
    return (
      <>
        <PopupMenu.Section title="Presets">
          <PopupMenu.Item
            key="none"
            title="None"
            onClick={() => editAssetBackground("clear")}
            checked={selectedPreset === "clear"}
            image={{ color: "transparent" }}
          />
          <PopupMenu.Item
            key="light"
            title="Light (default)"
            onClick={() => editAssetBackground("light")}
            checked={selectedPreset === "light" || selectedPreset === "default" || !selectedPreset}
            image={{ color: lightColor }}
          />
          <PopupMenu.Item
            key="dark"
            title="Dark"
            onClick={() => editAssetBackground("dark")}
            checked={selectedPreset === "dark"}
            image={{ color: darkColor }}
          />
          <PopupMenu.Item
            key="checkered"
            title="Checkered"
            onClick={() => editAssetBackground("checkered")}
            checked={selectedPreset === "checkered"}
            image={{ iconId: "content.checkered", fill: "gray", size: "16" }}
          />
        </PopupMenu.Section>
        <PopupMenu.Section title={recentColors.length > 0 ? "Recent colors" : null}>
          {recentColors.length > 0 && (
            <>
              {recentColors.map(color => (
                <PopupMenu.Item
                  key={color}
                  title={color}
                  onClick={() => editAssetBackground(color)}
                  checked={selectedPreset === color}
                  image={{ color }}
                />
              ))}
            </>
          )}
          <PopupMenu.Item
            key="custom"
            title="Custom"
            image={{ iconId: "content.custom-colors", fill: "", size: "16" }}
            checked={selectedPreset?.startsWith("#") && !recentColors.includes(selectedPreset)}>
            <Box onClick={e => e.stopPropagation()}>
              <StyledColorPicker
                color={pickerColor}
                onChangeComplete={onCustomColorChange}
                disableAlpha
              />
            </Box>
          </PopupMenu.Item>
        </PopupMenu.Section>
        {canEditTheme && (
          <PopupMenu.Section data-tooltip-source="theme-editor-button">
            <Flex background="grayLightest" p="8px" alignItems="center" flexDirection="column">
              <Text font="ui.small" textAlign="center">
                Configure preset colors in the
              </Text>
              <Button
                text="theme editor"
                buttonStyle="tertiary"
                size="small"
                disabled={mustUpgrade}
                {...themeEditorButtonProps}
              />
            </Flex>
            {mustUpgrade && (
              <UpsellTooltip
                onClick={themeAccess.openUpgradeModal}
                source="theme-editor-button"
                featureName="Custom themes"
                direction={Tooltip.Direction.Top}
              />
            )}
          </PopupMenu.Section>
        )}
      </>
    );
  }

  if (optionsOnly) {
    return renderMenu();
  }

  return (
    <PopupMenu.Item key="backgroundColor" title="Background color" hPos={hPos}>
      {renderMenu()}
    </PopupMenu.Item>
  );
};
